import { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { api } from "../../lib/api";
import type { Client, PageKey } from "@shared/types";
import logoUrl from "../../assets/gable-homes-logo.png";

const NAV: { to: string; label: string; icon: string; end: boolean; key: PageKey }[] = [
  { to: "/", label: "Executive Summary", icon: "▣", end: true, key: "summary" },
  { to: "/cash-flow", label: "Cash Flow", icon: "≈", end: false, key: "cash-flow" },
  { to: "/reconciliation", label: "Reconciliation", icon: "⇄", end: false, key: "reconciliation" },
  { to: "/tasks", label: "Tasks & Workflow", icon: "☑", end: false, key: "tasks" },
  { to: "/activity", label: "Activity", icon: "✎", end: false, key: "activity" },
];

export default function AppShell() {
  const { user, isBtzl, activeClientId, setActiveClientId, logout, canSee } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api
      .get<{ clients: Client[] }>("/api/clients")
      .then((d) => setClients(d.clients))
      .catch(() => setClients([]));
  }, []);

  const activeClient = clients.find((c) => c.id === activeClientId);
  const navItems = NAV.filter((item) => canSee(item.key));

  return (
    <div className="flex min-h-screen bg-ink-50">
      {/* Sidebar */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-ink-800 bg-ink-900 text-ink-100 md:flex">
        <div className="flex items-center gap-3 px-5 py-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white p-1 shadow-sm">
            <img src={logoUrl} alt="Gable Homes" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-white">Gable Homes</div>
            <div className="text-xs text-ink-400">{isBtzl ? "Portfolio · BTZL" : "Client portal"}</div>
          </div>
        </div>
        <nav className="mt-2 flex-1 space-y-1 px-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition focus-ring ${
                  isActive
                    ? "bg-accent text-white"
                    : "text-ink-300 hover:bg-ink-800 hover:text-white"
                }`
              }
            >
              <span className="w-4 text-center opacity-80">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-4 text-xs text-ink-500">
          {isBtzl ? "BTZL · full access" : "Client · reconcile & track tasks"}
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-ink-200 bg-white/90 px-6 py-3 backdrop-blur">
          <div className="flex items-center gap-3">
            {isBtzl && clients.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-ink-500">
                <span className="hidden sm:inline">Client</span>
                <select
                  value={activeClientId ?? ""}
                  onChange={(e) =>
                    setActiveClientId(e.target.value ? Number(e.target.value) : null)
                  }
                  className="rounded-lg border border-ink-300 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 focus-ring"
                >
                  <option value="">All clients</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!isBtzl && (
              <span className="text-sm font-medium text-ink-700">
                {activeClient?.name ?? user?.clientName ?? "Portfolio"}
              </span>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-ink-50 focus-ring"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink-900 text-xs font-semibold text-white">
                {user?.email?.[0]?.toUpperCase() ?? "?"}
              </span>
              <span className="hidden text-ink-700 sm:inline">{user?.email}</span>
              <span className="text-ink-400">▾</span>
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-52 rounded-lg border border-ink-200 bg-white p-1 shadow-card"
                onMouseLeave={() => setMenuOpen(false)}
              >
                <div className="px-3 py-2 text-xs text-ink-500">
                  Signed in as
                  <div className="truncate font-medium text-ink-800">{user?.email}</div>
                </div>
                <div className="my-1 h-px bg-ink-100" />
                <Link
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="block w-full rounded-md px-3 py-2 text-left text-sm text-ink-700 hover:bg-ink-50"
                >
                  {isBtzl ? "Settings & users" : "Settings"}
                </Link>
                <button
                  onClick={logout}
                  className="w-full rounded-md px-3 py-2 text-left text-sm text-ink-700 hover:bg-ink-50"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="min-w-0 flex-1 px-6 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
